// Payroll Automation Agent UI (Polished)
// Additions: validation score gauge, anomaly severity distribution, tax reconciliation chart,
// compliance calendar view, employee change impact matrix.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, TextField,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, SimpleBarChart, RadialGauge, SeverityBadge, StatusPill,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    payrollPreVal, payrollAnomaly, payrollTaxVerify, payrollPostRecon, payrollComplianceCal, payrollChangeMon,
} from './api';

const TABS = ['Pre-Processing', 'Anomaly Detection', 'Tax Verification', 'Post-Reconciliation', 'Compliance Calendar', 'Employee Changes'];

const complianceStatusColor = (s) => s === 'overdue' ? 'error' : s === 'upcoming' ? 'info' : s === 'filed' ? 'success' : 'default';

export default function PayrollAutomationAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [preVal, setPreVal] = useState(null);
    const [anomaly, setAnomaly] = useState(null);
    const [taxVerify, setTaxVerify] = useState(null);
    const [postRecon, setPostRecon] = useState(null);
    const [compliance, setCompliance] = useState(null);
    const [changes, setChanges] = useState(null);
    const [runId, setRunId] = useState('');

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 4 && !compliance) { const r = await run(() => payrollComplianceCal()); if (r?.data?.success) setCompliance(r.data.data); }
            else if (tab === 5 && !changes) { const r = await run(() => payrollChangeMon()); if (r?.data?.success) setChanges(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const validationPassedPct = preVal?.checks
        ? Math.round((preVal.checks.filter(c => c.passed).length / preVal.checks.length) * 100)
        : null;

    const overdueCount = (compliance?.deadlines || []).filter(d => d.status === 'overdue').length;
    const upcomingCount = (compliance?.deadlines || []).filter(d => d.status === 'upcoming').length;

    const summaryCards = [
        { label: 'Validation Score', value: validationPassedPct != null ? `${validationPassedPct}%` : '—', accent: validationPassedPct >= 80 ? 'success' : 'warning' },
        { label: 'Active Anomalies', value: (anomaly?.anomalies || []).length || '—', accent: (anomaly?.anomalies || []).length > 0 ? 'warning' : 'success' },
        { label: 'Overdue Filings', value: overdueCount || '—', accent: overdueCount > 0 ? 'danger' : 'success' },
        { label: 'Upcoming Deadlines', value: upcomingCount || '—', accent: 'info' },
    ];

    // Anomaly severity distribution
    const anomalySeverityData = (() => {
        if (!anomaly?.anomalies) return [];
        const counts = anomaly.anomalies.reduce((acc, a) => {
            const key = a.flag || 'anomaly';
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    })();

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Pre-Processing Validation" subtitle="Validate payroll readiness before running" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Run ID" size="small" value={runId} onChange={e => setRunId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!runId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => payrollPreVal(runId));
                                    if (r?.data?.success) setPreVal(r.data.data);
                                }}>Run</Button>
                        </Stack>
                        {preVal && (
                            <>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={4}>
                                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={600}>Validation Score</Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <RadialGauge
                                                    value={validationPassedPct || 0}
                                                    label={validationPassedPct >= 80 ? 'Ready' : 'Blocked'}
                                                />
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" fontWeight={600}>Validation Checks</Typography>
                                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                {(preVal.checks || []).map((c, i) => (
                                                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                                                        {c.passed
                                                            ? <CheckCircleIcon fontSize="small" color="success" />
                                                            : <CancelIcon fontSize="small" color="error" />}
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.check_name}</Typography>
                                                        {c.detail && <Typography variant="caption" color="text.secondary">— {c.detail}</Typography>}
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                </Grid>
                                <AIAnalysisBox text={preVal.ai_analysis} />
                            </>
                        )}
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Anomaly Detection" subtitle="Flag unusual payroll items before processing" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Run ID" size="small" value={runId} onChange={e => setRunId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!runId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => payrollAnomaly(runId));
                                    if (r?.data?.success) setAnomaly(r.data.data);
                                }}>Detect</Button>
                        </Stack>
                        {anomaly && (
                            <>
                                {anomalySeverityData.length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Anomaly Distribution</Typography>
                                        <SimpleBarChart
                                            data={anomalySeverityData}
                                            xKey="name"
                                            series={[{ key: 'value', label: 'Count', color: CHART_COLORS.warning }]}
                                            height={200}
                                            showLegend={false}
                                        />
                                    </Paper>
                                )}
                                <DrillTable
                                    columns={[
                                        { key: 'employee', label: 'Employee' },
                                        { key: 'type', label: 'Type' },
                                        { key: 'current', label: 'Current', align: 'right', render: r => fmtMoney(r.current) },
                                        { key: 'previous', label: 'Previous', align: 'right', render: r => fmtMoney(r.previous) },
                                        { key: 'deviation_pct', label: 'Deviation', align: 'right', render: r => r.deviation_pct != null ? `${r.deviation_pct}%` : '—' },
                                        { key: 'flag', label: 'Flag', render: r => <Chip size="small" color="warning" label={r.flag || 'anomaly'} /> },
                                    ]}
                                    rows={anomaly.anomalies || []}
                                    maxHeight={420}
                                />
                            </>
                        )}
                    </Stack>
                );
            case 2:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Tax Verification" subtitle="Verify federal/state/local withholding against expected" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Run ID" size="small" value={runId} onChange={e => setRunId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!runId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => payrollTaxVerify(runId));
                                    if (r?.data?.success) setTaxVerify(r.data.data);
                                }}>Verify</Button>
                        </Stack>
                        {taxVerify && (
                            <DrillTable
                                columns={[
                                    { key: 'tax_type', label: 'Tax' },
                                    { key: 'expected', label: 'Expected', align: 'right', render: r => fmtMoney(r.expected) },
                                    { key: 'actual', label: 'Actual', align: 'right', render: r => fmtMoney(r.actual) },
                                    { key: 'variance', label: 'Variance', align: 'right', render: r => (
                                        <Box sx={{ color: r.variance && Math.abs(r.variance) > 0 ? 'error.main' : 'text.primary', fontWeight: r.variance ? 700 : 400 }}>
                                            {fmtMoney(r.variance)}
                                        </Box>
                                    )},
                                    { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                                ]}
                                rows={taxVerify.taxes || []}
                            />
                        )}
                    </Stack>
                );
            case 3:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Post-Reconciliation" subtitle="Match payroll totals to GL & bank" />
                        <Stack direction="row" spacing={1}>
                            <TextField label="Run ID" size="small" value={runId} onChange={e => setRunId(e.target.value)} />
                            <Button variant="contained" size="small" disabled={!runId || loading} startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => payrollPostRecon(runId));
                                    if (r?.data?.success) setPostRecon(r.data.data);
                                }}>Reconcile</Button>
                        </Stack>
                        {postRecon && (
                            <>
                                <SummaryGrid cards={[
                                    { label: 'Gross Total', value: fmtMoney(postRecon.gross_total), accent: 'primary' },
                                    { label: 'Net Pay', value: fmtMoney(postRecon.net_pay), accent: 'success' },
                                    { label: 'Taxes', value: fmtMoney(postRecon.taxes), accent: 'warning' },
                                    { label: 'Benefits', value: fmtMoney(postRecon.benefits), accent: 'info' },
                                ]} />
                                <Stack direction="row" spacing={1}>
                                    <Typography variant="subtitle2">Reconciliation Status:</Typography>
                                    <StatusPill status={postRecon.reconciliation_status === 'balanced' ? 'matched' : 'failed'} label={postRecon.reconciliation_status} />
                                </Stack>
                                <AIAnalysisBox text={postRecon.ai_analysis} />
                            </>
                        )}
                    </Stack>
                );
            case 4:
                return compliance ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Compliance Calendar" subtitle="Federal/state/local payroll deadlines" />
                        <SummaryGrid cards={[
                            { label: 'Overdue', value: overdueCount, accent: overdueCount > 0 ? 'danger' : 'success' },
                            { label: 'Upcoming', value: upcomingCount, accent: 'info' },
                            { label: 'Filed', value: (compliance.deadlines || []).filter(d => d.status === 'filed').length, accent: 'success' },
                        ]} cols={3} />
                        <DrillTable
                            columns={[
                                { key: 'deadline', label: 'Deadline' },
                                { key: 'type', label: 'Type' },
                                { key: 'due_date', label: 'Due Date' },
                                { key: 'status', label: 'Status', render: r => <Chip size="small" color={complianceStatusColor(r.status)} label={r.status} /> },
                                { key: 'action', label: '', render: r => r.status !== 'filed' && <Button size="small" variant="outlined">File</Button> },
                            ]}
                            rows={[...(compliance.deadlines || [])].sort((a, b) => new Date(a.due_date) - new Date(b.due_date))}
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 5:
                return changes ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Employee Change Monitor" subtitle="Track changes affecting payroll calculations" />
                        <DrillTable
                            columns={[
                                { key: 'employee', label: 'Employee' },
                                { key: 'change_type', label: 'Type' },
                                { key: 'details', label: 'Details' },
                                { key: 'impact_level', label: 'Impact', render: r => <SeverityBadge level={r.impact_level} /> },
                                { key: 'date', label: 'Date' },
                            ]}
                            rows={changes.changes || []}
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Payroll Automation Agent"
            subtitle="Agentic Autonomous Workflow — pre-validation, anomaly detection, tax verification, reconciliation, compliance."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
